//  ActionPlanModal.js

import React from 'react';
import { 
  X, ClipboardList, CheckCircle, Circle, Target, Calendar, 
  Clock, ArrowRight, Play, Check, AlertCircle 
} from 'lucide-react';

export const ActionPlanModal = ({ 
  show, 
  onClose, 
  actionPlan, 
  currentStepId, 
  onStepChange, 
  onStepComplete, 
  forceUpdate 
}) => {
  if (!show) return null;

  const completedSteps = actionPlan?.steps?.filter(step => step.termine).length || 0;
  const totalSteps = actionPlan?.steps?.length || 0;
  const progressPercentage = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  const handleStepClick = (stepId) => {
    onStepChange(stepId);
    // Optionnel : fermer le modal après sélection d'étape
    // onClose();
  };

  const handleCompleteStep = (stepId, event) => {
    event.stopPropagation(); // Empêcher la propagation vers le clic sur l'étape
    onStepComplete(stepId);
  };

  const getStepStatus = (step) => {
    if (step.termine) return 'completed';
    if (step.id === currentStepId) return 'current';
    return 'pending';
  };

  const getStepIcon = (step) => {
    const status = getStepStatus(step);
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'current':
        return <Play className="w-6 h-6 text-orange-600" />;
      default:
        return <Circle className="w-6 h-6 text-gray-400" />;
    }
  };

  const getStepStyles = (step) => {
    const status = getStepStatus(step);
    const baseStyles = "p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer hover:shadow-md";
    
    switch (status) {
      case 'completed':
        return `${baseStyles} bg-green-50 border-green-200 hover:bg-green-100`;
      case 'current':
        return `${baseStyles} bg-orange-50 border-orange-300 hover:bg-orange-100 ring-2 ring-orange-200`;
      default:
        return `${baseStyles} bg-gray-50 border-gray-200 hover:bg-gray-100`;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* En-tête du modal */}
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-amber-100 rounded-full flex items-center justify-center border-2 border-orange-200">
                <ClipboardList className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Plan d'Action</h2>
                <p className="text-gray-600">
                  {actionPlan?.titre || 'Votre feuille de route vers le succès'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
              title="Fermer"
            >
              <X size={24} className="text-gray-500" />
            </button>
          </div>

          {/* Barre de progression globale */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Progression globale</span>
              <span className="text-sm font-bold text-orange-600">{progressPercentage}%</span>
            </div>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-orange-400 to-green-500 transition-all duration-500 ease-out"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <div className="flex justify-between items-center mt-2 text-xs text-gray-600">
              <span>{completedSteps} étapes terminées</span>
              <span>{totalSteps - completedSteps} étapes restantes</span>
            </div>
          </div>
        </div>

        {/* Contenu du modal - Liste des étapes */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {actionPlan?.steps?.length > 0 ? (
            <div className="space-y-4" key={`steps-${forceUpdate}`}>
              {actionPlan.steps.map((step, index) => (
                <div
                  key={step.id}
                  className={getStepStyles(step)}
                  onClick={() => handleStepClick(step.id)}
                >
                  <div className="flex items-start gap-4">
                    {/* Icône et numéro d'étape */}
                    <div className="flex-shrink-0 flex flex-col items-center">
                      {getStepIcon(step)}
                      <span className="text-xs font-medium text-gray-500 mt-1">
                        {index + 1}
                      </span>
                    </div>

                    {/* Contenu de l'étape */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-800 mb-2">
                            {step.titre}
                          </h3>
                          
                          {step.description && (
                            <p className="text-gray-600 mb-3 text-sm leading-relaxed">
                              {step.description}
                            </p>
                          )}

                          {/* Métadonnées de l'étape */}
                          <div className="flex flex-wrap gap-3 text-xs">
                            {step.dureeEstimee && (
                              <span className="flex items-center gap-1 text-gray-500">
                                <Clock size={12} />
                                {step.dureeEstimee}
                              </span>
                            )}
                            
                            {step.priorite && (
                              <span className={`px-2 py-1 rounded-full font-medium ${
                                step.priorite === 'haute' 
                                  ? 'bg-red-100 text-red-700'
                                  : step.priorite === 'moyenne'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                Priorité {step.priorite}
                              </span>
                            )}

                            {step.ressourcesNecessaires && step.ressourcesNecessaires.length > 0 && (
                              <span className="flex items-center gap-1 text-gray-500">
                                <Target size={12} />
                                {step.ressourcesNecessaires.length} ressource(s)
                              </span>
                            )}
                          </div>

                          {/* Indicateur d'étape courante */}
                          {step.id === currentStepId && !step.termine && (
                            <div className="mt-3 flex items-center gap-2 text-orange-600 font-medium text-sm">
                              <ArrowRight size={14} />
                              <span>Étape courante</span>
                            </div>
                          )}
                        </div>

                        {/* Bouton d'action */}
                        <div className="flex-shrink-0 ml-4">
                          {step.termine ? (
                            <div className="flex items-center gap-2 text-green-600 font-medium text-sm">
                              <Check size={16} />
                              <span>Terminé</span>
                            </div>
                          ) : (
                            <button
                              onClick={(e) => handleCompleteStep(step.id, e)}
                              className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                              title="Marquer comme terminé"
                            >
                              Terminer
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Ressources nécessaires (développé) */}
                      {step.ressourcesNecessaires && step.ressourcesNecessaires.length > 0 && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <h4 className="text-sm font-medium text-blue-800 mb-2">
                            Ressources nécessaires :
                          </h4>
                          <ul className="text-sm text-blue-700 space-y-1">
                            {step.ressourcesNecessaires.map((ressource, idx) => (
                              <li key={idx} className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                                {ressource}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                Aucun plan d'action disponible
              </h3>
              <p className="text-gray-500">
                Commencez une conversation avec Aladinn pour générer votre plan d'action personnalisé.
              </p>
            </div>
          )}
        </div>

        {/* Pied du modal avec statistiques */}
        {actionPlan?.steps?.length > 0 && (
          <div className="bg-gray-50 p-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{completedSteps}</div>
                  <div className="text-xs text-gray-600">Terminées</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {currentStepId ? '1' : '0'}
                  </div>
                  <div className="text-xs text-gray-600">En cours</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-600">
                    {totalSteps - completedSteps - (currentStepId ? 1 : 0)}
                  </div>
                  <div className="text-xs text-gray-600">À faire</div>
                </div>
              </div>

              <div className="text-right">
                <div className="text-sm text-gray-600 mb-1">
                  Temps estimé restant
                </div>
                <div className="text-lg font-semibold text-gray-800">
                  {actionPlan.dureeEstimeeTotal || 'Non défini'}
                </div>
              </div>
            </div>

            {/* Bouton pour fermer ou action */}
            <div className="flex justify-end mt-4">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
              >
                Continuer la conversation
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};